import React from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
} from '@mui/material';
import {
  Zap,
  CheckCircle,
  ArrowRight,
  FileSearch,
  Database,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from 'oidc-react';

const Login: React.FC = () => {
  const auth = useAuth();

  const handleLogin = () => {
    auth.signIn();
  };

  const features = [
    {
      icon: <FileSearch size={28} />,
      title: 'Schnelle Erkennung',
      description: 'Potentiell geschützte Steine in Anleitungen sofort identifizieren',
      gradient: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    },
    {
      icon: <Zap size={28} />,
      title: 'Einfach & Schnell',
      description: 'PDF hochladen, analysieren, fertig - in Sekunden',
      gradient: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    },
    {
      icon: <Database size={28} />,
      title: 'Aktuelle Datenbank',
      description: 'Regelmäßig aktualisierte Liste bekannter Schutzrechte',
      gradient: 'linear-gradient(135deg, #10b981, #06b6d4)',
    },
  ];

  const stats = [
    { value: '50K+', label: 'Bekannte Teile' },
    { value: '<5s', label: 'Analysezeit' },
    { value: 'PDF/CSV', label: 'Upload-Formate' },
  ];

  return (
    <Box
      className="splash-bg noise"
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Floating Background Shapes */}
      <Box className="floating-shape shape-1" sx={{ top: '-10%', right: '-5%' }} />
      <Box className="floating-shape shape-2" sx={{ bottom: '-5%', left: '-10%' }} />
      <Box className="floating-shape shape-3" sx={{ top: '40%', left: '60%' }} />

      {/* Grid Pattern Overlay */}
      <Box
        className="grid-pattern"
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Navigation */}
        <Box
          className="animate-slideUp"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src="/ProtectedBricksLogo.svg"
              alt="Protected Bricks"
              sx={{
                width: 48,
                height: 48,
                filter: 'brightness(0) invert(1)',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 700,
                letterSpacing: '-0.02em',
              }}
            >
              Protected Bricks
            </Typography>
          </Box>
          <Button
            onClick={handleLogin}
            variant="outlined"
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              borderRadius: 3,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Anmelden
          </Button>
        </Box>

        {/* Hero Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            pt: { xs: 6, md: 10 },
            pb: { xs: 6, md: 8 },
          }}
        >
          {/* Badge */}
          <Box
            className="glass animate-slideUp stagger-1"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2.5,
              py: 1,
              borderRadius: 10,
              mb: 4,
            }}
          >
            <AlertCircle size={16} color="#fbbf24" />
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}
            >
              Hinweis-Tool - Keine Rechtsberatung
            </Typography>
          </Box>

          {/* Main Headline */}
          <Typography
            variant="h1"
            className="animate-slideUp stagger-2"
            sx={{
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: '-0.03em',
              color: 'white',
              mb: 3,
              maxWidth: 900,
            }}
          >
            Geschützte Steine
            <Box
              component="span"
              className="gradient-text"
              sx={{ display: 'block' }}
            >
              schnell erkennen
            </Box>
          </Typography>

          {/* Subheadline */}
          <Typography
            variant="h6"
            className="animate-slideUp stagger-3"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              fontWeight: 400,
              maxWidth: 600,
              mb: 5,
              lineHeight: 1.6,
            }}
          >
            Laden Sie Ihre Bauanleitungen hoch und erhalten Sie sofort Hinweise
            auf potentiell design-geschützte Klemmbausteine.
          </Typography>

          {/* CTA Button */}
          <Box
            className="animate-slideUp stagger-4"
            sx={{
              mb: 8,
            }}
          >
            <Button
              onClick={handleLogin}
              variant="contained"
              size="large"
              className="btn-glow"
              endIcon={<ArrowRight size={20} />}
              sx={{
                py: 2,
                px: 5,
                borderRadius: 3,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                boxShadow: '0 8px 32px rgba(37, 99, 235, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                },
              }}
            >
              Anleitung prüfen
            </Button>
          </Box>

          {/* Stats */}
          <Box
            className="glass animate-slideUp stagger-5"
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 3, sm: 6 },
              p: 3,
              borderRadius: 4,
            }}
          >
            {stats.map((stat, index) => (
              <Box key={index} sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: 'white',
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Features Section */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Typography
            variant="h3"
            className="animate-slideUp"
            sx={{
              textAlign: 'center',
              color: 'white',
              fontWeight: 700,
              mb: 2,
              letterSpacing: '-0.02em',
            }}
          >
            So einfach funktioniert es
          </Typography>
          <Typography
            variant="body1"
            className="animate-slideUp stagger-1"
            sx={{
              textAlign: 'center',
              color: 'rgba(255,255,255,0.6)',
              mb: 6,
              maxWidth: 500,
              mx: 'auto',
            }}
          >
            Hinweise auf potentiell geschützte Steine - schnell und unkompliziert
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {features.map((feature, index) => (
              <Box
                key={index}
                className={`glass-white feature-card animate-scaleIn stagger-${index + 2}`}
                sx={{
                  p: 4,
                  borderRadius: 4,
                  cursor: 'default',
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    background: feature.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    mb: 3,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    mb: 1,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#64748b', lineHeight: 1.6 }}
                >
                  {feature.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Benefits List */}
        <Box
          className="glass"
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            mb: 8,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 3,
            }}
          >
            {[
              'PDF und CSV Upload',
              'Automatische Teilenummer-Erkennung',
              'Detaillierte Schutzrecht-Informationen',
              'Export als Excel und PDF',
              'Unbegrenzte Analysen',
              'Deutscher Support',
            ].map((benefit, index) => (
              <Box
                key={index}
                className={`animate-slideUp stagger-${(index % 3) + 1}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #22c55e, #10b981)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CheckCircle size={14} color="white" />
                </Box>
                <Typography
                  variant="body1"
                  sx={{ color: 'rgba(255,255,255,0.9)' }}
                >
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Final CTA */}
        <Box
          className="glass-dark animate-scaleIn"
          sx={{
            textAlign: 'center',
            p: { xs: 5, md: 8 },
            borderRadius: 4,
            mb: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 700,
              mb: 2,
              letterSpacing: '-0.02em',
            }}
          >
            Jetzt Anleitungen prüfen
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              mb: 4,
              maxWidth: 500,
              mx: 'auto',
            }}
          >
            Laden Sie Ihre Bauanleitungen hoch und erhalten Sie
            sofort Hinweise auf potentiell geschützte Teile.
          </Typography>
          <Button
            onClick={handleLogin}
            variant="contained"
            size="large"
            className="btn-glow"
            endIcon={<ArrowRight size={20} />}
            sx={{
              py: 2,
              px: 5,
              borderRadius: 3,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: '0 8px 32px rgba(37, 99, 235, 0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              },
            }}
          >
            Kostenlos starten
          </Button>
        </Box>

        {/* Disclaimer */}
        <Box
          className="glass"
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 8,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}
          >
            <strong style={{ color: 'rgba(255,255,255,0.9)' }}>Hinweis:</strong> Protected Bricks bietet keine Rechtsberatung.
            Unsere Analyse dient ausschließlich als Hinweis auf potentiell design-geschützte Teile.
            Für verbindliche rechtliche Auskünfte wenden Sie sich bitte an einen Fachanwalt.
          </Typography>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.5)' }}
          >
            © {new Date().getFullYear()} Protected Bricks. Alle Rechte vorbehalten.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
